import React, { useState, useEffect, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { setActiveFeature, setActiveProject } from "../../actions/ui";
import {
    selectActiveFeature,
    selectSearchedLocation,
    selectFilteredProjects,
    selectBasemap,
    selectZoomToNsw,
} from "../../reducers/";
import { getIcon } from "../../modules/hub";
import { centerFromProject, projectShape } from "../../modules/project";
import pointInPolygon from "@turf/boolean-point-in-polygon";
import bboxPolygon from "@turf/bbox-polygon";
import turfPoint from "turf-point";
import styles from "./map.module.scss";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator } from "@deck.gl/core";

import { GeoJsonLayer } from "@deck.gl/layers";
import { StaticMap, SVGOverlay, Marker, NavigationControl } from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import Tooltip from "./tooltip";
import "mapbox-gl/dist/mapbox-gl.css";
import { feature as topoToGeo } from "topojson-client";
import { isMobile } from "react-device-detect";
import Bowser from "bowser";
const bowser = Bowser.getParser(window.navigator.userAgent);
const browser = bowser.getBrowser();
const isIE11 =
    browser.version === "11.0" && browser.name === "Internet Explorer";

const BBOX = [
    140.99927903600008,
    -37.50507657099996,
    153.63883173500005,
    -28.157020554999974,
];

const ICON_HEIGHT = 32;
const ICON_WIDTH = 24;
const CIRCLE_RADIUS = 4;

const createIsProjectInsideFeature = feature => {
    const featureBboxPoly = feature && bboxPolygon(feature.properties.bbox);

    return project => {
        if (!feature) return false;

        const point = turfPoint(centerFromProject(project));
        if (!pointInPolygon(point, featureBboxPoly)) {
            return false;
        }
        return pointInPolygon(point, feature);
    };
};

// const BLUE = "#0082be";
const BLUE = [0, 130, 190];
const BLUE_OPACITY = [0, 130, 190, 255 / 2];

const Map = props => {
    const initialViewport = {
        width: props.height,
        height: props.width,
    };
    const [hoverIcon, setHoverIcon] = useState();
    const [hoverCircle, setHoverCircle] = useState();
    const [hoveredFeature, setHoveredFeature] = useState();

    const initialViewState = React.useMemo(() => {
        const initialViewState = new WebMercatorViewport(
            initialViewport
        ).fitBounds([BBOX.slice(0, 2), BBOX.slice(2, 4)], {
            padding: 30,
        });
        const { width, height, ...rest } = initialViewState;

        return rest;
    }, []);
    const [viewState, setViewState] = useState(initialViewState);

    const {
        activeFeature,
        searchedLocation,
        basemap,
        projects,
        setActiveProject,
        geometryConfig,
    } = props;
    const { idSelector } = geometryConfig;

    const reset = React.useCallback(() => {
        if (
            viewState.latitude === initialViewState.latitude &&
            viewState.longitude === initialViewState.longitude &&
            viewState.zoom === initialViewState.zoom
        ) {
            return;
        }

        setViewState(x => {
            return {
                ...x,
                latitude: initialViewState.latitude,
                longitude: initialViewState.longitude,
                zoom: initialViewState.zoom,
                transitionInterpolator: new FlyToInterpolator(),
                transitionDuration: 200,
            };
        });
    });

    useEffect(() => {
        reset();
    }, [props.zoomToNsw]);

    useLayoutEffect(() => {
        if (!activeFeature) {
            reset();
            return;
        }

        setViewState(viewState => {
            const bbox = activeFeature.properties.bbox;

            const newViewState = new WebMercatorViewport({
                ...initialViewport,
                ...viewState,
            }).fitBounds([bbox.slice(0, 2), bbox.slice(2, 4)], {
                padding: { top: 10, bottom: 10, left: 10, right: 10 },
            });
            return {
                latitude: newViewState.latitude,
                longitude: newViewState.longitude,
                zoom: newViewState.zoom,
                transitionInterpolator: new FlyToInterpolator(),
                transitionDuration: 200,
            };
        });
    }, [activeFeature]);

    const setActiveFeatureIfNeeded = feature => {
        if (!feature) {
            if (activeFeature) {
                setActiveFeature(null);
            }
            return;
        }
        if (idSelector(feature) !== idSelector(activeFeature)) {
            props.setActiveFeature(feature);
        }
    };

    return (
        <DeckGL
            initialViewState={initialViewState}
            viewState={viewState}
            onViewStateChange={x => {
                setViewState(currentViewState => ({
                    ...currentViewState,
                    ...x.viewState,
                }));
            }}
            getCursor={({ isDragging }) => {
                if (hoveredFeature) {
                    return "pointer";
                }
                return isDragging ? "grabbing" : "grab";
            }}
            controller={{
                scrollZoom: false,
                touchZoom: false,
                dragPan: !isMobile,
            }}
            layers={[
                new GeoJsonLayer({
                    id: "electorates",
                    data: geometryConfig.geojsonUrl,
                    dataTransform: data => {
                        const geojson = topoToGeo(
                            data,
                            data.objects[geometryConfig.topologyObjectsName]
                        );
                        return geojson;
                    },
                    stroked: true,
                    lineWidthUnits: "pixels",
                    lineWidthMinPixels: 0.5,
                    getFillColor: d => {
                        const shouldFill =
                            idSelector(d) === idSelector(activeFeature);
                        return shouldFill ? BLUE_OPACITY : [255, 255, 255, 0];
                    },
                    highlightColor: BLUE_OPACITY,
                    getLineColor: BLUE,
                    pickable: true,
                    updateTriggers: {
                        getFillColor: isIE11 ? [] : [activeFeature],
                    },
                    autoHighlight: true,

                    onClick: d => {
                        setActiveFeatureIfNeeded(d.object);
                    },
                    onHover: d => {
                        setHoveredFeature(d.object);
                    },
                }),
            ]}
        >
            <StaticMap
                mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
                mapStyle={
                    basemap === "ROADMAP"
                        ? "mapbox://styles/mapbox/streets-v11"
                        : `mapbox://styles/mapbox/satellite-v9`
                }
            >
                <SVGOverlay
                    redraw={({ project }) => {
                        const isProjectInsideFeature = createIsProjectInsideFeature(
                            activeFeature
                        );

                        return projects.map((d, i) => {
                            const [x, y] = project(centerFromProject(d));
                            const region = activeFeature
                                ? idSelector(activeFeature)
                                : "no region";

                            if (isProjectInsideFeature(d)) {
                                return (
                                    <image
                                        onClick={e => setActiveProject(d)}
                                        onMouseEnter={e => {
                                            if (isMobile) {
                                                return;
                                            }
                                            setHoverIcon(d);
                                        }}
                                        onMouseLeave={e => setHoverIcon(null)}
                                        key={region + d.Name + i}
                                        xlinkHref={getIcon(d.hubName)}
                                        height={ICON_HEIGHT}
                                        x={x}
                                        y={y}
                                        width={ICON_WIDTH}
                                        data-key={
                                            "circle" + region + d.Name + i
                                        }
                                        style={{
                                            transform: `translate(-${ICON_WIDTH /
                                                2}px,-${ICON_HEIGHT}px)`,
                                        }}
                                    />
                                );
                            }
                            return (
                                <circle
                                    onClick={e => {
                                        setActiveProject(d);
                                    }}
                                    onMouseEnter={e => {
                                        if (isMobile) {
                                            return;
                                        }
                                        setHoverCircle(d);
                                    }}
                                    onMouseLeave={e => setHoverCircle(null)}
                                    key={"circle" + region + d.Name + i}
                                    data-key={"circle" + region + d.Name + i}
                                    cx={x}
                                    cy={y}
                                    r={CIRCLE_RADIUS}
                                    fill={"red"}
                                />
                            );
                        });
                    }}
                />

                {searchedLocation && (
                    <Marker
                        latitude={searchedLocation[1]}
                        longitude={searchedLocation[0]}
                    >
                        <img
                            style={{
                                transform: `translate(-50%, -50%)`,
                            }}
                            alt="Marker icon, a star"
                            src={process.env.PUBLIC_URL + "/img/star.svg"}
                        />
                    </Marker>
                )}
                <div className={styles.navigationControlBtns}>
                    <NavigationControl
                        onViewportChange={newViewState => {
                            setViewState(x => ({
                                ...x,
                                zoom: newViewState.zoom,
                                latitude: newViewState.latitude,
                                longitude: newViewState.longitude,
                                transitionInterpolator: new FlyToInterpolator(),
                                transitionDuration: 200,
                            }));
                        }}
                        showCompass={false}
                    />
                </div>
                <Tooltip
                    item={hoverCircle || hoverIcon}
                    offsetTop={
                        Boolean(hoverIcon) ? -ICON_HEIGHT : -CIRCLE_RADIUS
                    }
                />
            </StaticMap>
        </DeckGL>
    );
};

const featureShape = {
    type: PropTypes.string.isRequired,
    geometry: PropTypes.object.isRequired,
    properties: PropTypes.object.isRequired,
};
Map.propTypes = {
    projects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
    activeFeature: PropTypes.shape(featureShape),
    setActiveFeature: PropTypes.func.isRequired,
    setActiveProject: PropTypes.func.isRequired,
    searchedLocation: PropTypes.arrayOf(PropTypes.number),
    basemap: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    geometryConfig: PropTypes.object.isRequired,
    zoomToNsw: PropTypes.number.isRequired,
};

const mapStateToProps = state => ({
    activeFeature: selectActiveFeature(state),
    searchedLocation: selectSearchedLocation(state),
    projects: selectFilteredProjects(state),
    basemap: selectBasemap(state),
    zoomToNsw: selectZoomToNsw(state),
});
const mapDispatchToProps = { setActiveFeature, setActiveProject };

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js